/**
 * 기업 상세 — 목록에서 줄을 누르면 열린다
 * ---------------------------------------------------------------------------
 * 목록은 집계값만 보여 준다(기업 수·사용자 수·용량). 그래서 "이 회사 대표가
 * 누구더라", "사업자등록번호가 뭐였지" 를 보려면 갈 곳이 없었다. 지원 모드로
 * 들어가는 방법이 있지만 그건 고객 데이터에 손대는 일이라 감사로그가 남고,
 * 상호를 한 번 보려고 쓸 것이 아니다.
 *
 * 탭으로 나눈다. 뒤의 셋은 **해당하는 기록이 있을 때만** 뜬다.
 *   기업 정보      상호·대표자·주소·담당자 + 사업자등록증 같은 첨부 서류
 *   제품           이 기업이 쓰는 제품과 요금제·계약 인원
 *   결제 수단      지금 어떻게 내고 있는가 + 자동결제 동의 이력
 *   인원 변경      계약 인원을 넘겨 등록한 기록 — 청구서가 왜 늘었는지의 근거
 *   일괄등록 동의  엑셀로 구성원을 올릴 때 받은 개인정보 수집 동의
 *   서비스 종료    기업이 직접 낸 종료 신청
 *
 * ## 창이 스스로 스크롤한다
 * 기업 정보 탭은 입력 칸 19개에 첨부 서류 표까지 들어가 화면보다 길다.
 * 창에 높이를 주지 않았더니 **첨부 서류가 화면 아래로 잘려** 나갔고, 창
 * 안에는 스크롤 막대가 없어 "서류가 안 보인다" 가 되었다. 실제로 그렇게
 * 보고되었다 — 값은 다 내려와 있었는데 그릴 자리가 없었던 것이다.
 */

import { useQuery } from '@tanstack/react-query';
import { Alert, Descriptions, Modal, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { TenantProfileForm } from '../tenant/TenantProfileForm';

const { Text, Paragraph } = Typography;

interface TenantRowLike {
  tenantId: string;
  companyName: string;
  slug: string;
  status: string;
}

interface SeatApproval {
  approval_id: string;
  product_code: string;
  product_name: string | null;
  plan_code: string;
  previous_units: number;
  approved_units: number;
  unit_price: number;
  monthly_delta: number;
  reason: string;
  occasion: string;
  target_label: string | null;
  approved_at: string;
  approved_by_name: string | null;
}

/**
 * 이 기업이 쓰는 제품 한 줄.
 *
 * 칸 이름을 서버에 맞춘다. 예전에는 상태를 `status` 로 읽었는데 서버가 주는
 * 이름은 `subscriptionStatus` 라, **상태 열이 늘 「—」였다.** 값이 없는
 * 것처럼 보이지만 실제로는 내려오고 있었다 — 이런 어긋남은 화면만 봐서는
 * 알 수 없으니 이름을 서버와 같게 쓴다.
 */
interface TenantProductRow {
  productCode: string;
  productName: string;
  planCode: string | null;
  planName: string | null;
  subscriptionStatus: string | null;
  /** 계약 인원. 청구 금액의 기준이라 여기서 바로 보여 준다 */
  units: number | null;
  startedAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/** 구독 상태를 사람 말로. 모르는 값은 그대로 보여 준다 — 숨기면 더 모른다. */
const SUBSCRIPTION_LABEL: Record<string, { text: string; color?: string }> = {
  ACTIVE: { text: '사용 중', color: 'green' },
  TRIALING: { text: '무료 사용', color: 'blue' },
  PAST_DUE: { text: '미납', color: 'red' },
  SUSPENDED: { text: '정지', color: 'red' },
  CANCELED: { text: '해지', color: 'default' },
};

/** 기업이 낸 서비스 종료 신청 */
interface TerminationRequest {
  requestId: string;
  status: string;
  reasonCode: string | null;
  reasonText: string;
  products: string[];
  backupOptions: Array<{ code: string; name: string; price: number }>;
  backupFee: number;
  noticeAgreedAt: string | null;
  purgeScheduledAt: string | null;
  finalInvoiceNo: string | null;
  finalAmount: number | null;
  requestedAt: string;
  requestedByName: string | null;
  sourceIp: string | null;
  completedAt: string | null;
}

/**
 * 구성원 엑셀 일괄등록 한 번. 「누가 몇 명을 올리며 무엇에 동의했는가」
 *
 * 개인정보 분쟁이 생기면 가장 먼저 찾게 되는 기록이다. 동의 문구를
 * 버전만이 아니라 **원문 그대로** 들고 있어, 문구가 바뀐 뒤에도 그때
 * 무엇에 동의했는지가 남는다.
 */
interface BulkImport {
  importId: string;
  fileName: string;
  rowCount: number;
  createdCount: number;
  failedCount: number;
  consentVersion: string;
  consentText: string;
  actorName: string | null;
  actorIp: string | null;
  createdAt: string;
}

/**
 * 지금 어떻게 내고 있는가.
 *
 * 고객 화면이 쓰는 것과 **같은 서비스**가 만든 값이다. 운영자 화면이
 * 따로 계산하면 둘이 어긋나고, 어긋나면 어느 쪽이 맞는지 알 수 없다.
 */
interface PaymentMethodState {
  schedule: {
    payMethod: 'TRANSFER' | 'CARD';
    cardEnabled: boolean;
    payMethodRegistered: boolean;
    payMethodConfirmedAt: string | null;
    depositorName: string | null;
    invoiceIssueDay: number;
    dueDays: number;
    hasActiveCard: boolean;
  };
  cards: Array<{
    billingKeyId: string;
    methodType: string;
    cardIssuer: string | null;
    cardLast4: string | null;
    isDefault: boolean;
    status: string;
    registeredAt: string;
  }>;
}

/** 자동결제 동의 한 건. 「언제 얼마라고 안내받고 동의했는가」 */
interface AutopayConsent {
  consentId: string;
  agreedAt: string;
  agreedByName: string | null;
  sourceIp: string | null;
  monthlyAmount: number;
  vatAmount: number;
  totalAmount: number;
  quotedDetail: Array<{ description: string; amount: number }>;
  firstBillingAt: string | null;
  revokedAt: string | null;
  cardLabel: string | null;
}

/** 셀프 가입으로 들어온 기업의 동의·접속 기록. 운영자가 만든 기업은 null */
interface SignupRecord {
  signedUpAt: string | null;
  sourceIp: string | null;
  userAgent: string | null;
  termsAgreedAt: string | null;
  privacyAgreedAt: string | null;
  termsVersion: string | null;
  privacyVersion: string | null;
}

interface Props {
  tenant: TenantRowLike | null;
  onClose: () => void;
  /** 저장으로 목록의 값이 달라질 수 있다(상호 등) */
  onChanged?: () => void;
}

const won = (n: number) => Math.round(n).toLocaleString('ko-KR');

export function TenantDetailModal({ tenant, onClose, onChanged }: Props) {
  const open = Boolean(tenant);
  const id = tenant?.tenantId;

  const approvals = useQuery<SeatApproval[], NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'seat-approvals'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<SeatApproval[]>(`/admin/tenants/${id}/seat-approvals`)).data,
  });

  const terminations = useQuery<TerminationRequest[], NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'terminations'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<TerminationRequest[]>(`/admin/tenants/${id}/terminations`)).data,
  });

  const autopay = useQuery<AutopayConsent[], NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'autopay-consents'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<AutopayConsent[]>(`/admin/tenants/${id}/autopay-consents`)).data,
  });

  const bulkImports = useQuery<BulkImport[], NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'bulk-imports'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<BulkImport[]>(`/admin/tenants/${id}/bulk-imports`)).data,
  });

  const signup = useQuery<SignupRecord | null, NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'signup-record'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<SignupRecord | null>(`/admin/tenants/${id}/signup-record`)).data,
  });

  const payment = useQuery<PaymentMethodState, NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'payment-method'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<PaymentMethodState>(`/admin/tenants/${id}/payment-method`)).data,
  });

  const products = useQuery<TenantProductRow[], NormalizedError>({
    queryKey: ['admin', 'tenant', id, 'products'],
    enabled: open && Boolean(id),
    queryFn: async () =>
      (await apiClient.get<TenantProductRow[]>(`/admin/tenants/${id}/products`)).data,
  });

  return (
    <Modal
      open={open}
      title={
        <Space>
          <span>{tenant?.companyName}</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {tenant?.slug}
          </Text>
        </Space>
      }
      onCancel={() => {
        onChanged?.();
        onClose();
      }}
      footer={null}
      width={1060}
      style={{ top: 32 }}
      /*
        창 안에서 스크롤한다. 이것이 없으면 긴 탭(기업 정보)의 아래쪽 —
        하필 첨부 서류 — 이 화면 밖으로 밀려 보이지 않는다.
      */
      styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', paddingRight: 8 } }}
      destroyOnHidden
    >
      {id && (
        <Tabs
          /*
            줄 모양(기본)이면 글자만 늘어서 있어 탭으로 읽히지 않는다.
            카드 모양이면 「지금 어디에 있고 어디로 갈 수 있는지」가 보인다.
          */
          type="card"
          size="middle"
          items={[
            {
              key: 'profile',
              label: '기업 정보',
              children: (
                <TenantProfileForm
                  profilePath={`/admin/tenants/${id}/profile`}
                  docsPath={`/admin/tenants/${id}/documents`}
                  cacheKey={`admin:${id}`}
                />
              ),
            },
            {
              key: 'products',
              label: '제품',
              children: products.isError ? (
                <Alert type="error" showIcon message={errorText(products.error)} />
              ) : (
                <Table<TenantProductRow>
                  rowKey="productCode"
                  size="small"
                  pagination={false}
                  loading={products.isLoading}
                  dataSource={products.data ?? []}
                  locale={{ emptyText: '구독 중인 제품이 없습니다.' }}
                  columns={[
                    { title: '제품', dataIndex: 'productName' },
                    {
                      title: '요금제',
                      dataIndex: 'planName',
                      render: (v: string | null, row) => v ?? row.planCode ?? <Text type="secondary">—</Text>,
                    },
                    {
                      // 청구 금액의 기준이다. 「왜 이만큼 나왔나」를 여기서 먼저 본다.
                      title: '계약 인원',
                      dataIndex: 'units',
                      width: 90,
                      align: 'right',
                      render: (v: number | null) =>
                        v === null ? <Text type="secondary">—</Text> : `${v}명`,
                    },
                    {
                      title: '시작일',
                      dataIndex: 'startedAt',
                      width: 110,
                      render: (v: string | null) =>
                        v ? new Date(v).toLocaleDateString('ko-KR') : <Text type="secondary">—</Text>,
                    },
                    {
                      title: '이번 주기 종료',
                      dataIndex: 'currentPeriodEnd',
                      width: 130,
                      render: (v: string | null, row) =>
                        v ? (
                          <Space direction="vertical" size={0}>
                            <span>{new Date(v).toLocaleDateString('ko-KR')}</span>
                            {row.cancelAtPeriodEnd && <Tag color="orange">이후 해지</Tag>}
                          </Space>
                        ) : (
                          <Text type="secondary">—</Text>
                        ),
                    },
                    {
                      title: '상태',
                      dataIndex: 'subscriptionStatus',
                      width: 100,
                      render: (v: string | null) => {
                        if (!v) return <Text type="secondary">—</Text>;
                        const label = SUBSCRIPTION_LABEL[v];
                        return <Tag color={label?.color}>{label?.text ?? v}</Tag>;
                      },
                    },
                  ]}
                />
              ),
            },
            ...((terminations.data ?? []).length > 0
              ? [
                  {
                    key: 'termination',
                    label: <span style={{ color: '#cf1322' }}>서비스 종료</span>,
                    /*
                      신청이 있을 때만 탭을 띄운다. 대부분의 기업에는 없는
                      일이고, 빈 탭이 늘 떠 있으면 「종료」가 일상적인 말처럼
                      보인다.
                    */
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                          기업이 직접 낸 종료 신청입니다. 무엇을 끝냈고, 무엇을 백업 신청했고,
                          왜 그만뒀는지가 담겨 있습니다.
                        </Paragraph>

                        {(terminations.data ?? []).map((t) => (
                          <Descriptions
                            key={t.requestId}
                            bordered
                            size="small"
                            column={2}
                            title={
                              <Space>
                                <Tag color={t.status === 'COMPLETED' ? 'red' : 'orange'}>
                                  {t.status === 'COMPLETED' ? '종료 완료' : t.status}
                                </Tag>
                                <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                                  {new Date(t.requestedAt).toLocaleString('ko-KR')} 신청
                                </Text>
                              </Space>
                            }
                          >
                            <Descriptions.Item label="끝낸 서비스" span={2}>
                              {t.products.length > 0 ? (
                                <Space wrap size={4}>
                                  {t.products.map((p) => (
                                    <Tag key={p}>{p}</Tag>
                                  ))}
                                </Space>
                              ) : (
                                <Text type="secondary">—</Text>
                              )}
                            </Descriptions.Item>

                            <Descriptions.Item label="백업 신청" span={2}>
                              {t.backupOptions.length > 0 ? (
                                <Space direction="vertical" size={0}>
                                  {t.backupOptions.map((b) => (
                                    <span key={b.code}>
                                      · {b.name} — {won(b.price)}
                                    </span>
                                  ))}
                                  <Text strong>합계 {won(t.backupFee)}</Text>
                                </Space>
                              ) : (
                                <Text type="secondary">신청하지 않음</Text>
                              )}
                            </Descriptions.Item>

                            <Descriptions.Item label="종료 사유" span={2}>
                              <Space direction="vertical" size={0}>
                                {t.reasonCode && <Tag>{t.reasonCode}</Tag>}
                                <Text>{t.reasonText}</Text>
                              </Space>
                            </Descriptions.Item>

                            <Descriptions.Item label="마지막 정산">
                              {t.finalAmount === null ? (
                                <Text type="secondary">정산할 금액 없음</Text>
                              ) : (
                                <Space direction="vertical" size={0}>
                                  <strong>{won(t.finalAmount)}</strong>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t.finalInvoiceNo}
                                  </Text>
                                </Space>
                              )}
                            </Descriptions.Item>
                            <Descriptions.Item label="자료 삭제 예정">
                              {t.purgeScheduledAt
                                ? new Date(t.purgeScheduledAt).toLocaleDateString('ko-KR')
                                : '—'}
                            </Descriptions.Item>

                            <Descriptions.Item label="안내문 동의">
                              {t.noticeAgreedAt ? (
                                <Space direction="vertical" size={0}>
                                  <Tag color="green">동의함</Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {new Date(t.noticeAgreedAt).toLocaleString('ko-KR')}
                                  </Text>
                                </Space>
                              ) : (
                                <Tag>기록 없음</Tag>
                              )}
                            </Descriptions.Item>
                            <Descriptions.Item label="신청자 · IP">
                              {t.requestedByName ?? '—'}
                              {t.sourceIp && (
                                <>
                                  {' · '}
                                  <Text code>{t.sourceIp}</Text>
                                </>
                              )}
                            </Descriptions.Item>
                          </Descriptions>
                        ))}
                      </Space>
                    ),
                  },
                ]
              : []),
            ...((bulkImports.data ?? []).length > 0
              ? [
                  {
                    key: 'bulk-imports',
                    label: `일괄등록 동의 (${bulkImports.data!.length})`,
                    /*
                      올린 적이 없는 기업이 대부분이라, 기록이 있을 때만
                      탭을 띄운다. 빈 탭이 늘 떠 있으면 무슨 탭인지 궁금해
                      열어 보고 아무것도 없는 일이 반복된다.
                    */
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                          엑셀로 구성원을 한꺼번에 올릴 때, 올린 사람이 「개인정보를
                          수집할 근거가 있다」고 확인한 기록입니다. 아래 문구는{' '}
                          <b>동의 당시 화면에 떠 있던 원문</b>이라, 나중에 문구가 바뀌어도
                          그대로 남습니다.
                        </Paragraph>

                        {bulkImports.isError && (
                          <Alert type="error" showIcon message={errorText(bulkImports.error)} />
                        )}

                        {(bulkImports.data ?? []).map((b) => (
                          <Descriptions
                            key={b.importId}
                            bordered
                            size="small"
                            column={2}
                            labelStyle={{ width: 110 }}
                          >
                            <Descriptions.Item label="올린 때">
                              {new Date(b.createdAt).toLocaleString('ko-KR')}
                            </Descriptions.Item>
                            <Descriptions.Item label="올린 사람">
                              {b.actorName ?? <Text type="secondary">(삭제된 계정)</Text>}
                              {b.actorIp && (
                                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                  {b.actorIp}
                                </Text>
                              )}
                            </Descriptions.Item>
                            <Descriptions.Item label="파일">{b.fileName}</Descriptions.Item>
                            <Descriptions.Item label="결과">
                              <Space size={4}>
                                <Tag color="green">등록 {b.createdCount}명</Tag>
                                {b.failedCount > 0 && <Tag color="red">실패 {b.failedCount}</Tag>}
                                <Text type="secondary">/ 전체 {b.rowCount}줄</Text>
                              </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="동의 문구" span={2}>
                              <Text style={{ fontSize: 12 }}>{b.consentText}</Text>
                              <div>
                                <Tag style={{ marginTop: 6 }}>{b.consentVersion}</Tag>
                              </div>
                            </Descriptions.Item>
                          </Descriptions>
                        ))}
                      </Space>
                    ),
                  },
                ]
              : []),
            {
              key: 'autopay',
              label: (
                <Space size={6}>
                  <span>결제 수단</span>
                  {payment.data &&
                    (payment.data.schedule.payMethodRegistered ? (
                      <Tag color="green" style={{ marginInlineEnd: 0 }}>
                        {payment.data.schedule.payMethod === 'CARD' ? '카드' : '계좌이체'}
                      </Tag>
                    ) : (
                      <Tag color="red" style={{ marginInlineEnd: 0 }}>
                        미등록
                      </Tag>
                    ))}
                </Space>
              ),
              /*
                ## 왜 「자동결제」가 아니라 「결제 수단」인가
                예전에는 자동결제 **동의 이력**만 보여 줬다. 그런데 그 기록은
                **카드를 등록할 때만** 남는다. 지금 운영은 계좌이체라 모든
                기업의 이력이 비어 있었고, 화면은 어느 기업이든 똑같이
                「동의한 기록이 없습니다」만 보여 줬다 — **계좌이체로 꼬박꼬박
                내는 기업과 아무것도 등록하지 않은 기업이 구별되지 않았다.**

                그래서 **지금 상태**를 맨 위에 둔다. 이력은 그 아래다.
              */
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {payment.isError ? (
                    <Alert type="error" showIcon message={errorText(payment.error)} />
                  ) : (
                    payment.data && (
                      <Descriptions
                        bordered
                        size="small"
                        column={2}
                        labelStyle={{ width: 130 }}
                        title={
                          <Space>
                            <span>지금 어떻게 내고 있나</span>
                            {payment.data.schedule.payMethodRegistered ? (
                              <Tag color="green">등록됨</Tag>
                            ) : (
                              <Tag color="red">등록 필요</Tag>
                            )}
                          </Space>
                        }
                      >
                        <Descriptions.Item label="결제 수단">
                          <Tag
                            color={payment.data.schedule.payMethod === 'CARD' ? 'blue' : 'default'}
                          >
                            {payment.data.schedule.payMethod === 'CARD'
                              ? '카드 자동결제'
                              : '계좌이체'}
                          </Tag>
                          {!payment.data.schedule.cardEnabled && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              카드 결제는 아직 열려 있지 않습니다
                            </Text>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="고른 때">
                          {payment.data.schedule.payMethodConfirmedAt ? (
                            new Date(payment.data.schedule.payMethodConfirmedAt).toLocaleString(
                              'ko-KR',
                            )
                          ) : (
                            <Text type="secondary">아직 고르지 않았습니다</Text>
                          )}
                        </Descriptions.Item>

                        {/*
                          입금자명은 통장에 찍히는 이름이다. 상호와 다른 일이
                          흔해(대표자 개인 이름·경리 담당자 이름) 이것이 없으면
                          입금 내역을 보고도 어느 기업이 낸 돈인지 모른다.
                        */}
                        <Descriptions.Item label="입금자명">
                          {payment.data.schedule.depositorName ? (
                            <Text strong>{payment.data.schedule.depositorName}</Text>
                          ) : (
                            <Text type="secondary">
                              적지 않음 — 상호로 들어온다고 보고 대사합니다
                            </Text>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="청구·납기">
                          매월 {payment.data.schedule.invoiceIssueDay}일 발행 · 발행 후{' '}
                          {payment.data.schedule.dueDays}일 이내
                        </Descriptions.Item>

                        <Descriptions.Item label="등록된 카드" span={2}>
                          {payment.data.cards.length === 0 ? (
                            <Text type="secondary">없음</Text>
                          ) : (
                            <Space direction="vertical" size={2}>
                              {payment.data.cards.map((c) => (
                                <Space key={c.billingKeyId} size={6}>
                                  <Text>{c.cardIssuer ?? c.methodType}</Text>
                                  {c.cardLast4 && (
                                    <Text type="secondary">**** {c.cardLast4}</Text>
                                  )}
                                  {c.isDefault && <Tag color="blue">기본</Tag>}
                                  <Tag color={c.status === 'ACTIVE' ? 'green' : undefined}>
                                    {c.status === 'ACTIVE' ? '사용' : c.status}
                                  </Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {new Date(c.registeredAt).toLocaleDateString('ko-KR')} 등록
                                  </Text>
                                </Space>
                              ))}
                            </Space>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    )
                  )}

                  <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    자동결제 동의 이력
                  </Typography.Title>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    카드를 등록할 때 보여 준 안내와 그에 대한 동의입니다. 금액은{' '}
                    <b>동의 당시에 안내한 값</b>이라, 요금제가 바뀌어도 그대로 남습니다.{' '}
                    <b>계좌이체로 내는 기업은 이 기록이 없습니다</b> — 카드에서만 받는
                    동의이기 때문입니다.
                  </Paragraph>

                  {autopay.isError && (
                    <Alert type="error" showIcon message={errorText(autopay.error)} />
                  )}

                  <Table<AutopayConsent>
                    rowKey="consentId"
                    size="small"
                    pagination={false}
                    loading={autopay.isLoading}
                    dataSource={autopay.data ?? []}
                    locale={{
                      emptyText:
                        '카드 자동결제에 동의한 기록이 없습니다. 계좌이체 기업은 비어 있는 것이 정상입니다.',
                    }}
                    expandable={{
                      expandedRowRender: (row) => (
                        <Table
                          rowKey={(r: { description: string }) => r.description}
                          size="small"
                          pagination={false}
                          dataSource={row.quotedDetail}
                          locale={{ emptyText: '안내 항목이 없습니다.' }}
                          columns={[
                            { title: '안내한 항목', dataIndex: 'description' },
                            {
                              title: '금액',
                              dataIndex: 'amount',
                              width: 120,
                              align: 'right',
                              render: (v: number) => `${won(v)}원`,
                            },
                          ]}
                        />
                      ),
                    }}
                    columns={[
                      {
                        title: '동의 일시',
                        dataIndex: 'agreedAt',
                        width: 160,
                        render: (v: string) => new Date(v).toLocaleString('ko-KR'),
                      },
                      {
                        title: '결제수단',
                        dataIndex: 'cardLabel',
                        width: 130,
                        render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                      },
                      {
                        title: '안내한 월 청구액',
                        dataIndex: 'totalAmount',
                        width: 140,
                        align: 'right',
                        render: (v: number) => <strong>{won(v)}원</strong>,
                      },
                      {
                        title: '첫 청구 예정',
                        dataIndex: 'firstBillingAt',
                        width: 130,
                        render: (v: string | null) =>
                          v ? new Date(v).toLocaleDateString('ko-KR') : '—',
                      },
                      {
                        title: '동의자',
                        dataIndex: 'agreedByName',
                        width: 100,
                        render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                      },
                      {
                        title: '접속 IP',
                        dataIndex: 'sourceIp',
                        width: 130,
                        render: (v: string | null) =>
                          v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>,
                      },
                      {
                        title: '상태',
                        dataIndex: 'revokedAt',
                        width: 110,
                        render: (v: string | null) =>
                          v ? (
                            <Tooltip title={`${new Date(v).toLocaleString('ko-KR')} 해지`}>
                              <Tag>해지됨</Tag>
                            </Tooltip>
                          ) : (
                            <Tag color="green">유효</Tag>
                          ),
                      },
                    ]}
                  />
                </Space>
              ),
            },
            {
              key: 'signup',
              label: '가입 기록',
              /*
                동의를 받는 것만으로는 부족하다. **언제, 어느 버전에,
                어디에서** 동의했는지가 남아야 분쟁에서 설명할 수 있다.
                운영자가 직접 만든 기업은 가입 기록이 없다 — 그때
                「동의 안 함」으로 보이면 안 되므로 따로 적는다.
              */
              children: signup.isLoading ? (
                <Text type="secondary">불러오는 중…</Text>
              ) : !signup.data ? (
                <Alert
                  type="info"
                  showIcon
                  message="셀프 가입으로 만들어진 기업이 아닙니다"
                  description="운영자가 직접 등록했거나 약관 동의 절차가 생기기 전에 만들어진 기업입니다. 가입 당시의 동의·접속 기록이 없습니다."
                />
              ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="가입 완료">
                      {signup.data.signedUpAt
                        ? new Date(signup.data.signedUpAt).toLocaleString('ko-KR')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="접속 IP">
                      <Text code>{signup.data.sourceIp ?? '—'}</Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="이용약관 동의">
                      {signup.data.termsAgreedAt ? (
                        <Space direction="vertical" size={0}>
                          <Tag color="green">동의함</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(signup.data.termsAgreedAt).toLocaleString('ko-KR')}
                            {signup.data.termsVersion ? ` · ${signup.data.termsVersion}` : ''}
                          </Text>
                        </Space>
                      ) : (
                        <Tag>기록 없음</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="개인정보처리방침 동의">
                      {signup.data.privacyAgreedAt ? (
                        <Space direction="vertical" size={0}>
                          <Tag color="green">동의함</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(signup.data.privacyAgreedAt).toLocaleString('ko-KR')}
                            {signup.data.privacyVersion ? ` · ${signup.data.privacyVersion}` : ''}
                          </Text>
                        </Space>
                      ) : (
                        <Tag>기록 없음</Tag>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="브라우저" span={2}>
                      <Text style={{ fontSize: 12, wordBreak: 'break-all' }}>
                        {signup.data.userAgent ?? '—'}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>

                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    가입 화면에서 받은 동의입니다. 동의 시각은 화면이 보낸 값이 아니라{' '}
                    <b>서버가 찍은 시각</b>이라, 기기 시계가 틀려도 기록은 정확합니다.
                  </Paragraph>
                </Space>
              ),
            },
            {
              key: 'seats',
              label: `인원 변경${approvals.data?.length ? ` (${approvals.data.length})` : ''}`,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    계약 인원을 넘겨 구성원을 등록할 때 받은 승인입니다. 청구 금액이 언제, 왜,
                    누구의 승인으로 늘었는지를 보여 줍니다 — 고객사에 설명할 수 있어야 하는
                    기록입니다.
                  </Paragraph>

                  {approvals.isError && (
                    <Alert type="error" showIcon message={errorText(approvals.error)} />
                  )}

                  <Table<SeatApproval>
                    rowKey="approval_id"
                    size="small"
                    pagination={false}
                    loading={approvals.isLoading}
                    dataSource={approvals.data ?? []}
                    locale={{ emptyText: '계약 인원을 넘겨 등록한 기록이 없습니다.' }}
                    expandable={{
                      expandedRowRender: (row) => (
                        <Descriptions size="small" column={1} bordered>
                          <Descriptions.Item label="사유">{row.reason}</Descriptions.Item>
                          <Descriptions.Item label="대상">
                            {row.target_label ?? '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="경로">{row.occasion}</Descriptions.Item>
                          <Descriptions.Item label="당시 1인당 금액">
                            {won(row.unit_price)}원
                          </Descriptions.Item>
                        </Descriptions>
                      ),
                    }}
                    columns={[
                      {
                        title: '승인 일시',
                        dataIndex: 'approved_at',
                        width: 160,
                        render: (v: string) => new Date(v).toLocaleString('ko-KR'),
                      },
                      {
                        title: '제품',
                        dataIndex: 'product_name',
                        width: 130,
                        render: (v: string | null, row) => v ?? row.product_code,
                      },
                      {
                        title: '계약 인원',
                        key: 'units',
                        width: 120,
                        render: (_, row) => (
                          <>
                            {row.previous_units} → <strong>{row.approved_units}</strong>
                          </>
                        ),
                      },
                      {
                        title: '월 증가액',
                        dataIndex: 'monthly_delta',
                        width: 110,
                        align: 'right',
                        render: (v: number) => <Text type="danger">+{won(v)}원</Text>,
                      },
                      {
                        title: '승인자',
                        dataIndex: 'approved_by_name',
                        width: 110,
                        render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
                      },
                      {
                        title: '사유',
                        dataIndex: 'reason',
                        ellipsis: true,
                      },
                    ]}
                  />
                </Space>
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
}
